import React, { useMemo, useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ItemModal from "../components/ItemModal";
import { useApi } from "../utils/Api";
import { Helmet } from "react-helmet-async";

const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

export default function MenuPage() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeCategory, setActiveCategory] = useState("");
  const [search, setSearch] = useState("");

  const categoryRefs = useRef({});
  const categoryScrollRef = useRef(null);

  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { apiFetch } = useApi();

  const formatMoney = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(Number(n || 0));

  const fetchCategories = React.useCallback(async () => {
    const data = await apiFetch("categories");
    setCategories(Array.isArray(data) ? data : []);
  }, [apiFetch]);

  const fetchMenuItems = React.useCallback(async () => {
    const data = await apiFetch("menu-items");
    setMenuItems(Array.isArray(data) ? data : []);
  }, [apiFetch]);

  // Initial load (wait for both calls)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        await Promise.all([fetchCategories(), fetchMenuItems()]);
      } catch (err) {
        console.error("Menu load error:", err);
        if (!cancelled) {
          setCategories([]);
          setMenuItems([]);
          setError("Failed to load menu. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchCategories, fetchMenuItems]);

  const menu = useMemo(() => {
    if (!categories.length) return [];

    return categories.map((cat) => {
      // Items directly in this category
      const directItems = menuItems
        .filter((item) => Number(item.category_id) === Number(cat.id))
        .map((item) => ({
          id: item.id,
          name: item.item_name,
          description: item.item_description,
          price: Number(item.price),
          image: item.image,
          in_stock: item.in_stock,
          required_options: item.required_options || [],
          optional_options: item.optional_options || [],
        }));

      // Items in subcategories
      const subItems =
        cat.subcategories?.flatMap((sub) =>
          menuItems
            .filter((item) => Number(item.category_id) === Number(sub.id))
            .map((item) => ({
              id: item.id,
              name: item.item_name,
              description: item.item_description,
              price: Number(item.price),
              image: item.image,
              in_stock: item.in_stock,
              subcategory: sub.category_name,
              required_options: item.required_options || [],
              optional_options: item.optional_options || [],
            })),
        ) || [];

      return {
        id: String(cat.id),
        title: cat.category_name,
        items: [...directItems, ...subItems],
      };
    });
  }, [categories, menuItems]);

  // Set default active category when menu loads
  useEffect(() => {
    if (menu.length > 0 && !activeCategory) {
      setActiveCategory(menu[0].id);
    }
  }, [menu, activeCategory]);

  // Filter menu by search
  const filteredMenu = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return menu;

    return menu
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((item) =>
          `${item.name} ${item.description} ${cat.title} ${
            item.subcategory || ""
          }`
            .toLowerCase()
            .includes(q),
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [menu, search]);

  // Track active category based on scroll
  useEffect(() => {
    if (loading) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      for (const cat of filteredMenu) {
        const ref = categoryRefs.current[cat.id];
        if (ref) {
          const { offsetTop, offsetHeight } = ref;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveCategory(cat.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [filteredMenu, loading]);

  // Scroll category nav to keep active tab visible
  useEffect(() => {
    if (loading) return;

    const container = categoryScrollRef.current;
    if (!container) return;

    const activeBtn = container.querySelector(`[data-cat="${activeCategory}"]`);
    if (!activeBtn) return;

    activeBtn.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeCategory, loading]);

  const scrollToCategory = (id) => {
    const ref = categoryRefs.current[id];
    if (!ref) return;

    const navHeight = categoryScrollRef.current?.offsetHeight || 0;
    const offsetTop = ref.offsetTop - navHeight - 90;

    window.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-[#f9f9f9] py-12 font-sans">
      <Helmet>
  <title>Menu | Dee Delicious Afro-Caribbean Restaurant</title>

  <meta
    name="description"
    content="Explore the Dee Delicious menu featuring authentic Nigerian cuisines and Jamaican dishes in Liverpool including jollof rice, suya, soups, and Caribbean favourites."
  />

  <link rel="canonical" href="https://deedelicious.com/menu" />

  <meta property="og:title" content="Menu | Dee Delicious Afro-Caribbean Restaurant, Liverpool" />
  <meta property="og:description" content="Discover Nigerian and Jamaican dishes freshly prepared at Dee Delicious." />
  <meta property="og:url" content="https://deedelicious.com/menu" />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://deedelicious.com/static/media/logo.06d3da75fe2458d68075.png" />

  <meta name="twitter:card" content="summary_large_image" />
</Helmet>

      {/* Spinner */}
      {loading && <SpinnerOverlay />}

      <div className="mx-auto max-w-6xl px-6 flex flex-col gap-8">
        {/* Notice */}
        <div className="relative overflow-hidden rounded-3xl border-l-8 border-secondary bg-primary/5 p-6 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary text-white">
              ⚠
            </div>
            <p className="text-lg font-bold text-secondary tracking-wide uppercase">
              Notice
            </p>
          </div>
          <ul className="space-y-1 text-sm text-gray-700 leading-relaxed">
            <li>
              • Locally sourced ingredients &{" "}
              <span className="font-semibold">100% Halal Food</span>
            </li>
            <li>
              • If you have a food allergy, intolerance, or coeliac disease,
              please speak to our staff before ordering as our ingredients may
              contain peanuts, milk, wheat, dairy and poultry.
            </li>
          </ul>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search meals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm shadow-sm focus:border-secondary-500 focus:ring-0 transition-all"
        />

        {/* Category Nav */}
        <div className="sticky top-20 z-[5] bg-[#f3f3f3] p-4 flex items-center gap-2">
          <button
            className="block md:hidden p-2 rounded-full bg-black/5 hover:bg-black/10 transition text-gray-600"
            onClick={() =>
              categoryScrollRef.current?.scrollBy({
                left: -180,
                behavior: "smooth",
              })
            }
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>

          <div
            ref={categoryScrollRef}
            className="category-scroll flex gap-3 overflow-x-auto flex-1"
          >
            {filteredMenu.map((cat) => {
              const activeState = cat.id === activeCategory;

              return (
                <button
                  key={cat.id}
                  data-cat={cat.id}
                  onClick={() => scrollToCategory(cat.id)}
                  className={`flex items-center gap-2 whitespace-nowrap px-5 py-3 rounded-xl font-semibold transition-all ${
                    activeState
                      ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                      : "bg-white text-stone-600 hover:bg-orange-50"
                  }`}
                >
                  {cat.title}
                </button>
              );
            })}
          </div>

          <button
            className="block md:hidden p-2 rounded-full bg-black/5 hover:bg-black/10 transition text-gray-600"
            onClick={() =>
              categoryScrollRef.current?.scrollBy({
                left: 180,
                behavior: "smooth",
              })
            }
          >
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
        </div>

        {/* Error */}
        {!loading && error && (
          <div className="text-center text-red-500 py-10">{error}</div>
        )}

        {/* Only show “No items found” after loading is finished */}
        {!loading && !error && filteredMenu.length === 0 && (
          <div className="text-center text-gray-500 py-16">No items found.</div>
        )}

        {/* Items */}
        {!loading &&
          !error &&
          filteredMenu.map((cat) => (
            <div
              key={cat.id}
              ref={(el) => (categoryRefs.current[cat.id] = el)}
              className="pt-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {cat.title}
              </h2>

              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {cat.items.map((item) => (
                  <div
                    key={item.id}
                    className="relative bg-[#f6f6f6] rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer flex flex-col"
                    onClick={() => {
                      if (!item.in_stock) return;
                      setSelectedItem(item);
                      setModalOpen(true);
                    }}
                  >
                    {item.image && (
                      <img
                        src={`https://deedelicious.com/${item.image}`}
                        alt={item.name}
                        className="w-full h-56 object-cover"
                        loading="lazy"
                      />
                    )}

                    {!item.in_stock && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xl font-bold rounded-3xl">
                        Out of Stock
                      </div>
                    )}

                    <div className="p-6 flex flex-col flex-1">
                      <div>
                        <div className="flex justify-between gap-3">
                          <h3 className="text-lg font-bold text-gray-900">
                            {item.name}
                          </h3>
                          <p className="text-lg font-bold text-orange-500 whitespace-nowrap">
                            {formatMoney(item.price)}
                          </p>
                        </div>

                        <p className="mt-3 text-sm text-gray-600">
                          {item.description}
                        </p>

                        {item.subcategory && (
                          <p className="mt-3 inline-block text-xs font-semibold px-3 py-1 rounded-full bg-black/5 text-gray-700">
                            {item.subcategory}
                          </p>
                        )}
                      </div>

                      <div className="mt-auto pt-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!item.in_stock) return;
                            setSelectedItem(item);
                            setModalOpen(true);
                          }}
                          className={`w-full flex items-center justify-center gap-2 rounded-2xl py-2 font-semibold transition ${
                            item.in_stock
                              ? "bg-primary/10 text-primary hover:bg-primary hover:text-white"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          <span className="text-lg">＋</span>{" "}
                          {item.in_stock ? "Add to Order" : "Unavailable"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

        {/* Modal */}
        <ItemModal
          item={selectedItem}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </div>
    </div>
  );
}
